import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Check, X, ArrowLeft, Package } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';

const UNITS = ['litre', 'kg', 'gram', 'piece', 'dozen', 'packet', 'bottle'];

function SettingsPage() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // inline add/edit state
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', unit: 'litre', default_price: '' });

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getProductItems();
      setItems(res.data || []);
    } catch (err) {
      setError('Failed to load items: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ name: '', unit: 'litre', default_price: '' });
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      unit: item.unit,
      default_price: item.default_price != null ? item.default_price.toString() : ''
    });
    setShowForm(true);
  };

  const handleDelete = async (item) => {
    if (item.is_default) { toast.warning('Default items cannot be deleted.'); return; }
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      setLoading(true);
      await apiService.deleteProductItem(item.item_id);
      await load();
    } catch (err) {
      toast.error('Failed to delete: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.warning('Item name is required.'); return; }
    if (!formData.unit) { toast.warning('Unit is required.'); return; }

    const payload = {
      name: formData.name.trim(),
      unit: formData.unit,
      default_price: formData.default_price !== '' ? Number(formData.default_price) : null
    };

    try {
      setLoading(true);
      setError(null);
      if (editingItem) {
        await apiService.updateProductItem(editingItem.item_id, payload);
      } else {
        await apiService.createProductItem(payload);
      }
      setShowForm(false);
      setEditingItem(null);
      await load();
    } catch (err) {
      toast.error('Failed to save: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
    setError(null);
  };

  return (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">

          <div className="page-header">
            <div className="header-left">
              <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Package size={22} /> Settings — Products & Units
              </h1>
            </div>
            {!showForm && (
              <button className="btn btn-primary" onClick={handleAdd} disabled={loading}>
                <Plus size={18} /> Add Product
              </button>
            )}
          </div>

          {error && (
            <div className="error-container">
              <p>{error}</p>
              <button className="btn btn-secondary btn-sm" onClick={() => setError(null)}>Dismiss</button>
            </div>
          )}

          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Define the products you sell and their default prices. <strong>Buffalo Milk</strong> and <strong>Cow Milk</strong> are built-in defaults
            and cannot be deleted. Custom items (e.g. Curd, Ghee) can be added here and will appear as optional line items when creating a sale.
          </p>

          {/* Inline form */}
          {showForm && (
            <div className="card" style={{ maxWidth: '560px', marginBottom: '1.5rem' }}>
              <h3 style={{ marginBottom: '1rem', fontSize: '1rem' }}>{editingItem ? 'Edit Product' : 'Add New Product'}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Product Name *</label>
                  <input
                    className="form-input"
                    value={formData.name}
                    onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Curd, Ghee, Butter"
                    disabled={loading || (editingItem?.is_default)}
                  />
                  {editingItem?.is_default && (
                    <small style={{ color: 'var(--text-secondary)' }}>Default item — name cannot be changed</small>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Unit *</label>
                  <select
                    className="form-select"
                    value={formData.unit}
                    onChange={e => setFormData(p => ({ ...p, unit: e.target.value }))}
                    disabled={loading || (editingItem?.is_default)}
                  >
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Default Price (₹ per {formData.unit})</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.default_price}
                    onChange={e => setFormData(p => ({ ...p, default_price: e.target.value }))}
                    placeholder="Optional — can override per sale"
                    min="0"
                    step="0.01"
                    disabled={loading}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button className="btn btn-secondary" onClick={handleCancel} disabled={loading}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                  <Check size={16} /> {loading ? 'Saving...' : (editingItem ? 'Update' : 'Add Product')}
                </button>
              </div>
            </div>
          )}

          {/* Items table */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Unit</th>
                  <th>Default Price</th>
                  <th>Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.item_id}>
                    <td style={{ fontWeight: 500 }}>{item.name}</td>
                    <td>{item.unit}</td>
                    <td>{item.default_price != null ? `₹${item.default_price}` : '—'}</td>
                    <td>
                      <span style={{
                        padding: '2px 10px', borderRadius: '12px', fontSize: '0.8rem',
                        background: item.is_default ? '#dbeafe' : '#dcfce7',
                        color: item.is_default ? '#1d4ed8' : '#166534'
                      }}>
                        {item.is_default ? 'Default' : 'Custom'}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <button className="btn-icon" onClick={() => handleEdit(item)} disabled={loading} title="Edit">
                          <Edit size={16} />
                        </button>
                        {!item.is_default && (
                          <button className="btn-icon" style={{ color: 'var(--error-color)' }} onClick={() => handleDelete(item)} disabled={loading} title="Delete">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length === 0 && !loading && (
              <div className="no-data"><p>No products found. Add your first product above.</p></div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
